/**
 * Utility functions for clinic-related operations
 */

/**
 * Generate a slugified clinic name
 * @param {string} clinicName - The clinic name
 * @returns {string} - Slugified name
 */
export const slugifyClinicName = (clinicName) => {
    if (!clinicName) return '';
    
    return clinicName
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
        .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Generate clinic URL slug with clinic ID
 * @param {string} clinicName - The clinic name
 * @param {number|string} clinicId - The clinic ID
 * @returns {string} - Formatted slug like "clinic-name-123"
 */
export const generateClinicSlug = (clinicName, clinicId) => {
    const slugifiedName = slugifyClinicName(clinicName);
    return `${slugifiedName}-${clinicId}`;
};

/**
 * Extract clinic ID from slug
 * @param {string} slug - The slug like "clinic-name-123"
 * @returns {string} - The clinic ID
 */
export const extractClinicIdFromSlug = (slug) => {
    if (!slug) return null;
    
    // Find the last hyphen and get everything after it
    const lastHyphenIndex = slug.lastIndexOf('-');
    if (lastHyphenIndex === -1) return null;
    
    return slug.substring(lastHyphenIndex + 1);
};

/**
 * Generate clinic URL based on context
 * @param {string} clinicName - The clinic name
 * @param {number|string} clinicId - The clinic ID
 * @param {string} context - Either 'onboard' or 'manage'
 * @returns {string} - The full URL
 */
export const generateClinicUrl = (clinicName, clinicId, context) => {
    const slug = generateClinicSlug(clinicName, clinicId);
    
    if (context === 'onboard') {
        return `/onboard/clinics/clinic-details/${slug}`;
    } else if (context === 'manage') {
        return `/manage/clinic/clinic-details/${slug}`;
    }
    
    // Fallback to old format
    return `/clinics/${clinicId}`;
}; 